import { Tier, EducationalInvestment } from '@backrs_ui/common-ui';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import type { OwnershipLevel } from '@backrs_ui/grow-ui/src/utils/determineOwnership';

const DIAGRAM_STRINGS = appStrings.modules.results.diagram;
const OWNERSHIP_STRINGS = appStrings.modules.survey.output.blueprintSection.ownership.detail;

type BlueprintSectionContext = Readonly<{
  tier: Tier;
  careerPathSuggestions: CareerPathWithScore[];
  educationalInvestment?: { target: EducationalInvestment[]; others: EducationalInvestment[] };
  ownershipLevel: OwnershipLevel;
}>;

export const useAgentContext = ({
  tier,
  careerPathSuggestions,
  educationalInvestment,
  ownershipLevel,
}: BlueprintSectionContext) => {
  useRegisterAgentContext({
    kind: 'visual',
    label: 'Minimum Career Track',
    display: `The user's minimum recommended career track is ${tier}.`,
    detail: DIAGRAM_STRINGS[tier]?.tooltip ?? '',
    data: { minimumCareerTrack: tier },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Educational Investments',
    display:
      'Education pathways tied to the user\'s minimum career track. "Target" investments are the recommended path; "others" are alternatives that do not match the track as well.',
    data: {
      target: (educationalInvestment?.target ?? []).map((inv) => ({
        name: inv.name,
        description: inv.description,
      })),
      others: (educationalInvestment?.others ?? []).map((inv) => ({
        name: inv.name,
        description: inv.description,
      })),
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Career Paths',
    display:
      "Career paths bucketed by fit (strong/medium/low) based on the user's preferences for meaning, activities, and work setting.",
    data: {
      strongFit: careerPathSuggestions
        .filter((p) => p.scoreSection === 'TOP')
        .map((p) => p.careerPath.name),
      mediumFit: careerPathSuggestions
        .filter((p) => p.scoreSection === 'MIDDLE')
        .map((p) => p.careerPath.name),
      lowFit: careerPathSuggestions
        .filter((p) => p.scoreSection === 'LOW')
        .map((p) => p.careerPath.name),
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Ownership',
    display: `The user's ownership level is "${ownershipLevel}".`,
    detail: [
      `${OWNERSHIP_STRINGS.definingOwnershipBadge}: ${OWNERSHIP_STRINGS.definingOwnershipBody}`,
      `${OWNERSHIP_STRINGS.whyItMattersLabel}${OWNERSHIP_STRINGS.definingOwnershipWhyItMatters}`,
      `What "${ownershipLevel}" means for this user: ${OWNERSHIP_STRINGS.taglines[ownershipLevel]}`,
      `${OWNERSHIP_STRINGS.rewardsHeading}: ${OWNERSHIP_STRINGS.rewards.join('; ')}`,
      `${OWNERSHIP_STRINGS.challengesHeading}: ${OWNERSHIP_STRINGS.challenges.join('; ')}`,
    ].join('\n\n'),
    data: { ownershipLevel },
  });
};
